import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { 
  getFeatureFlag, 
  isFeatureEnabled, 
  startSessionRecording, 
  stopSessionRecording, 
  showSurvey,
  trackEvent,
  trackUserJourney 
} from '../lib/posthog';

interface PostHogFeaturesProps {
  eventId?: string;
  inviteId?: string;
  userRole?: 'host' | 'guest' | 'public';
}

export default function PostHogFeatures({ eventId, inviteId, userRole = 'guest' }: PostHogFeaturesProps) {
  const router = useRouter();
  const [featuresLoaded, setFeaturesLoaded] = useState(false);

  useEffect(() => {
    // Wait a bit for PostHog to initialize and load feature flags
    const timeout = setTimeout(() => {
      setFeaturesLoaded(true);
      
      // Check if session recording should be enabled
      const enableRecording = isFeatureEnabled('enable-session-recordings');
      const recordingRate = getFeatureFlag('session-recording-rate') || 0.1;
      
      if (enableRecording && Math.random() < Number(recordingRate)) {
        startSessionRecording();
        
        trackEvent('session_recording_started', {
          event_id: eventId,
          invite_id: inviteId,
          user_role: userRole,
          page_path: router.asPath,
        });
      }

      // Track page-specific user journey
      const pageType = getPageType(router.pathname);
      trackUserJourney(`page_visited_${pageType}`, {
        event_id: eventId,
        invite_id: inviteId,
        user_role: userRole,
        page_path: router.asPath,
      });

      // Check for surveys based on user behavior
      checkForSurveys();
      
    }, 2000); // 2 second delay to ensure PostHog is loaded

    return () => clearTimeout(timeout);
  }, [eventId, inviteId, userRole, router.asPath, router.pathname]);

  // Check if surveys should be shown
  const checkForSurveys = () => {
    // Show feedback survey for hosts
    if (userRole === 'host' && isFeatureEnabled('host-feedback-survey')) {
      const surveyShown = localStorage.getItem('host-survey-shown');
      if (!surveyShown) {
        setTimeout(() => {
          showSurvey('host-feedback');
          localStorage.setItem('host-survey-shown', 'true');
        }, 10000); // Show after 10 seconds
      }
    }

    // Show RSVP experience survey for guests who just RSVP'd
    if (userRole === 'guest' && router.asPath.includes('rsvp=completed')) {
      if (isFeatureEnabled('rsvp-experience-survey')) {
        setTimeout(() => {
          showSurvey('rsvp-experience');
        }, 3000); // Show after 3 seconds
      }
    }

    // Show general user experience survey randomly
    if (isFeatureEnabled('general-ux-survey')) {
      const surveyRate = getFeatureFlag('ux-survey-rate') || 0.05; // 5% by default
      const lastSurvey = localStorage.getItem('last-ux-survey');
      const daysSinceLastSurvey = lastSurvey 
        ? (Date.now() - parseInt(lastSurvey)) / (1000 * 60 * 60 * 24)
        : 30; // Assume 30 days if never shown

      if (Math.random() < Number(surveyRate) && daysSinceLastSurvey > 7) {
        setTimeout(() => {
          showSurvey('general-ux');
          localStorage.setItem('last-ux-survey', Date.now().toString());
        }, 30000); // Show after 30 seconds
      }
    }
  };

  // Track specific interactions
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Track important button clicks
      if (target.tagName === 'BUTTON' || target.closest('button')) {
        const button = target.tagName === 'BUTTON' ? target : target.closest('button');
        const buttonText = button?.textContent?.trim();
        const buttonClass = button?.className;

        // Track RSVP-related clicks
        if (buttonText?.toLowerCase().includes('rsvp') || 
            buttonClass?.includes('rsvp') ||
            buttonText?.toLowerCase().includes('attending') ||
            buttonText?.toLowerCase().includes('not attending') ||
            buttonText?.toLowerCase().includes('maybe')) {
          
          trackEvent('important_button_click', {
            button_text: buttonText,
            button_type: 'rsvp_related',
            event_id: eventId,
            invite_id: inviteId,
            user_role: userRole,
            page_path: router.asPath,
          });
        }

        // Track share-related clicks
        if (buttonText?.toLowerCase().includes('share') || 
            buttonClass?.includes('share')) {
          
          trackEvent('important_button_click', {
            button_text: buttonText,
            button_type: 'share',
            event_id: eventId,
            invite_id: inviteId,
            user_role: userRole,
            page_path: router.asPath,
          });
        }

        // Track calendar-related clicks
        if (buttonText?.toLowerCase().includes('calendar') || 
            buttonClass?.includes('calendar')) {
          
          trackEvent('important_button_click', {
            button_text: buttonText,
            button_type: 'calendar',
            event_id: eventId,
            invite_id: inviteId,
            user_role: userRole,
            page_path: router.asPath,
          });
        }
      }

      // Track link clicks
      if (target.tagName === 'A' || target.closest('a')) {
        const link = target.tagName === 'A' ? target : target.closest('a');
        const href = link?.getAttribute('href');
        const linkText = link?.textContent?.trim();

        if (href) {
          trackEvent('link_click', {
            link_href: href,
            link_text: linkText,
            is_external: href.startsWith('http') && !href.includes(window.location.hostname),
            event_id: eventId,
            invite_id: inviteId,
            user_role: userRole,
            page_path: router.asPath,
          });
        }
      }
    };

    // Track form submissions
    const handleSubmit = (event: SubmitEvent) => {
      const form = event.target as HTMLFormElement;
      const formId = form.id || 'unknown';
      const formAction = form.action || 'unknown';

      trackEvent('form_submission', {
        form_id: formId,
        form_action: formAction,
        event_id: eventId,
        invite_id: inviteId,
        user_role: userRole,
        page_path: router.asPath,
      });
    };

    // Add event listeners if features are loaded
    if (featuresLoaded) {
      document.addEventListener('click', handleClick);
      document.addEventListener('submit', handleSubmit);

      return () => {
        document.removeEventListener('click', handleClick);
        document.removeEventListener('submit', handleSubmit);
      };
    }
  }, [featuresLoaded, eventId, inviteId, userRole, router.asPath]);

  // Track page performance
  useEffect(() => {
    if (featuresLoaded && typeof window !== 'undefined' && window.performance) {
      const measurePerformance = () => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          trackEvent('page_performance', {
            load_time: navigation.loadEventEnd - navigation.loadEventStart,
            dom_content_loaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
            first_paint: performance.getEntriesByName('first-paint')[0]?.startTime || 0,
            first_contentful_paint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime || 0,
            event_id: eventId,
            invite_id: inviteId,
            user_role: userRole,
            page_path: router.asPath,
          });
        }
      };

      // Wait for page to fully load
      if (document.readyState === 'complete') {
        measurePerformance();
      } else {
        window.addEventListener('load', measurePerformance);
        return () => window.removeEventListener('load', measurePerformance);
      }
    }
  }, [featuresLoaded, eventId, inviteId, userRole, router.asPath]);

  return null; // This component doesn't render anything
}

// Helper function to determine page type
function getPageType(pathname: string): string {
  if (pathname.includes('/i/')) return 'invite';
  if (pathname.includes('/p/')) return 'public';
  if (pathname.includes('/e/')) return 'event';
  if (pathname === '/') return 'home';
  return 'other';
}

// Hook for other components to trigger surveys manually
export function usePostHogSurveys() {
  const triggerSurvey = (surveyId: string, delay = 0) => {
    if (delay > 0) {
      setTimeout(() => showSurvey(surveyId), delay);
    } else {
      showSurvey(surveyId);
    }
  };

  const triggerFeedbackSurvey = () => {
    triggerSurvey('feedback', 1000);
  };

  const triggerNPSSurvey = () => {
    triggerSurvey('nps', 2000);
  };

  return {
    triggerSurvey,
    triggerFeedbackSurvey,
    triggerNPSSurvey,
  };
}
